import { useEffect, useRef, useState } from "react";

// Animates a KPI tile's number toward its real value over ~600ms whenever
// that value changes. The one place a JS animation earns its cost over CSS
// here — CSS cannot interpolate a number's text content.
//
// Reads `target`/`from` through refs inside the rAF loop rather than the
// tick closure's captured value, so a rapid second value change (loading
// flips a tile's value to 0 mid-animation, then the real next value arrives
// moments later — exactly what happens on every filter change here) can't
// leave a stale closure's tick still writing an old target's numbers after
// a newer effect has already taken over.
//
// Browsers throttle/suspend requestAnimationFrame for a backgrounded or
// non-visible tab (an admin can absolutely have MCAP open in a background
// tab while working elsewhere) — rAF simply never calls back in that case.
// A setTimeout deadline set to the animation's own duration guarantees the
// real value always lands even when rAF never fires, instead of a tile
// silently freezing on a stale or zero number until the tab regains focus.
export default function useCountUp(value, { duration = 600 } = {}) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);

  const targetRef = useRef(target);
  const fromRef = useRef(target);
  const startRef = useRef(null);
  const frameRef = useRef(null);
  const deadlineRef = useRef(null);

  useEffect(() => {
    if (targetRef.current === target) return undefined;

    fromRef.current = display; // animate from whatever is currently on screen, not from 0
    targetRef.current = target;
    startRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(fromRef.current + (targetRef.current - fromRef.current) * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    deadlineRef.current = setTimeout(() => setDisplay(targetRef.current), duration + 50);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return display;
}
